import { G } from '@mobily/ts-belt';
import { useGetOne } from 'react-admin';

import type { FinancialIndexShortName } from '@Common/constants/financial_indexes';
import type { GetOpsActionsIndexesIdResponse } from '@Common/types/bo/ops_actions/indexes';

import { useFinancialIndex } from '@sh4res/bo-common/hooks/useFinancialIndex';

export function useHistoricalFinancialIndexValue(
  shortName: FinancialIndexShortName,
  date: string | null,
): { value: number | null; isLoaded: boolean } {
  const { resource, index: listedIndex, isFetched: isListFetched } = useFinancialIndex(shortName);
  const indexId = listedIndex?.id;

  const {
    data: index,
    isFetched: isShowFetched,
    isError,
  } = useGetOne<GetOpsActionsIndexesIdResponse>(
    resource,
    {
      id: indexId ?? 0,
    },
    { enabled: G.isNotNullable(indexId) },
  );

  if (G.isNullable(date)) {
    return { value: null, isLoaded: false };
  }

  if (!isListFetched || (G.isNotNullable(indexId) && !isShowFetched && !isError)) {
    return { value: null, isLoaded: false };
  }

  if (G.isNullable(index)) {
    return { value: null, isLoaded: true };
  }

  // react-admin can seed the getOne cache with partial list records. BO apps set
  // `cacheTime: 0` to avoid this, but it's a race, so guard against a list item
  // lacking the show-only fields until the detail request completes.
  if (!Array.isArray(index.historical_values) || !Array.isArray(index.planned_updates)) {
    return { value: null, isLoaded: isError };
  }

  const versions = [
    ...index.historical_values,
    {
      value: index.value,
      effective_date: index.effective_date,
    },
    ...index.planned_updates,
  ];
  const [asOf] = versions
    .filter((version) => version.effective_date <= date)
    .sort((left, right) => right.effective_date.localeCompare(left.effective_date));

  return { value: G.isNullable(asOf) ? null : asOf.value, isLoaded: true };
}
